import React, { useCallback, useEffect, useRef, useState } from 'react'
import {
  Accordion,
  AccordionDetails,
  AccordionSummary,
  Button,
  Grid,
  List,
  ListItem,
  ListItemText,
  Typography,
  useTheme,
} from '@mui/material'
import ExpandMoreIcon from '@mui/icons-material/ExpandMore'
import CanvasDatagrid from './CanvasDatagrid'
import useStyles from './styles'
import validators from '../../../../../../utils/validators'
import NoticeBanner from '../../../../../NoticeBanner'
import { ReactComponent as WarningIcon } from '../../../../../../assets/Icons_Warning.svg'
import { useTranslation } from 'react-i18next'

// Map backend field names to column indices
const fieldToColumnMap = {
  nChassis: 0,
  nParc: 1,
  vehicleType: 2,
  typeVehicule: 2,
  member: 3,
  operator: 4,
  depot: 5,
  location: 6,
  lieu: 6,
}

// Map column indices back to workbook row keys (for grid data updates)
const columnIndexToWorkbookKey = {
  0: 'nChassis',
  1: 'nParc',
  2: 'typeVehicule',
  3: 'beneficiaire',
  4: 'operateur',
  5: 'depot',
  6: 'lieu',
}

// Map column indices to backend field names (for recording corrections)
const columnIndexToField = {
  2: 'vehicleType',
  3: 'member',
  4: 'operator',
  5: 'depot',
  6: 'location',
}

const fieldLabels = {
  vehicleType: 'Type véhicule',
  operator: 'Opérateur',
  depot: 'Dépôt',
  member: 'Bénéficiaire',
  location: 'Lieu',
}

function getErrors(rowsData) {
  const newErrors = new Set()
  rowsData.forEach((row, i) => {
    if (row.nChassis == null || row.nChassis === '') {
      newErrors.add(`${i}-0`)
    } else if (!validators.isValidNChassis(row.nChassis)) {
      newErrors.add(`${i}-0`)
    }
    if (row.nParc == null || row.nParc === '') {
      newErrors.add(`${i}-1`)
    } else if (!validators.isValidNParc(row.nParc)) {
      newErrors.add(`${i}-1`)
    }
    if (row.typeVehicule == null || row.typeVehicule === '') {
      newErrors.add(`${i}-2`)
    }
  })
  return newErrors
}

function processValidationErrors(validationErrors) {
  const errorSet = new Set()
  const errorMap = {}

  validationErrors.forEach((error) => {
    if (error.row && error.field) {
      const dataRowIndex = error.row - 2
      const columnIndex = fieldToColumnMap[error.field]
      if (dataRowIndex >= 0 && columnIndex !== undefined) {
        const key = `${dataRowIndex}-${columnIndex}`
        errorSet.add(key)
        if (!errorMap[key]) errorMap[key] = []
        errorMap[key].push(error.message)
      }
    }
  })

  return { errorSet, errorMap }
}

function processCorrections(corrections) {
  const correctionMap = {}
  corrections.forEach((correction) => {
    if (!correction.row || !correction.field) return
    const dataRowIndex = correction.row - 2
    const columnIndex = fieldToColumnMap[correction.field]
    if (dataRowIndex < 0 || columnIndex === undefined) return
    const key = `${dataRowIndex}-${columnIndex}`
    correctionMap[key] = {
      field: correction.field,
      originalValue: correction.originalValue,
      correctedValue: correction.correctedValue,
      type: correction.type,
    }
  })
  return correctionMap
}

export default function PreviewFileStep({
  workbookData,
  details,
  validationErrors = [],
  corrections = [],
  onConfirm = () => {},
  onManualCorrection = () => {},
}) {
  const classes = useStyles()
  const theme = useTheme()
  const { t } = useTranslation()
  const canvasRef = useRef(null)
  const gridContainerRef = useRef(null)

  // Direct DOM manipulation (synchronous) to block mouse events on the grid
  // while the accordion layout shifts — avoids canvas-datagrid's dragResizeColumn crash
  const blockGridPointerEvents = () => {
    if (gridContainerRef.current)
      gridContainerRef.current.style.pointerEvents = 'none'
  }
  const restoreGridPointerEvents = () => {
    if (gridContainerRef.current)
      gridContainerRef.current.style.pointerEvents = ''
    canvasRef.current?.draw?.()
  }

  const [errors, setErrors] = useState({
    set: new Set(),
    count: 0,
    validationErrorMap: {},
  })
  const [correctionMap, setCorrectionMap] = useState({})

  useEffect(() => {
    if (!workbookData) return

    const clientErrorsSet = getErrors(workbookData)
    const { errorSet: backendErrorsSet, errorMap } =
      processValidationErrors(validationErrors)

    const newCorrectionMap = processCorrections(corrections)

    // Suggestions are informational (yellow cells) but do not block import
    const combinedErrors = new Set([...clientErrorsSet, ...backendErrorsSet])

    setErrors({
      set: combinedErrors,
      count: combinedErrors.size,
      validationErrorMap: errorMap,
    })
    setCorrectionMap(newCorrectionMap)
  }, [workbookData, validationErrors, corrections])

  const autoCorrections = corrections.filter((c) => c.type === 'auto')
  const suggestionCorrections = corrections.filter(
    (c) => c.type === 'suggestion'
  )
  const autoCorrectionsCount = autoCorrections.length
  const suggestionsCount = suggestionCorrections.length
  const totalCorrectionsCount = autoCorrectionsCount + suggestionsCount

  // Point 3: revert an auto-correction back to the original value
  const handleRevertCorrection = useCallback(
    (rowIndex, columnIndex, originalValue) => {
      const colKey = columnIndexToWorkbookKey[columnIndex]
      if (!colKey) return

      const gridData = canvasRef.current && canvasRef.current.data
      if (gridData && gridData[rowIndex]) {
        gridData[rowIndex][colKey] = originalValue
        canvasRef.current.data = gridData
        if (canvasRef.current.draw) canvasRef.current.draw()
      }

      const key = `${rowIndex}-${columnIndex}`
      setCorrectionMap((prev) => {
        const next = { ...prev }
        delete next[key]
        return next
      })
      // The original value is invalid — re-add it as an error
      setErrors((prev) => {
        const newSet = new Set(prev.set)
        newSet.add(key)
        return { ...prev, set: newSet, count: newSet.size }
      })
    },
    []
  )

  // Point 4: called from CanvasDatagrid when user edits a suggestion cell
  const handleManualCorrection = useCallback(
    (rowIndex, columnIndex, originalValue, newValue) => {
      const field = columnIndexToField[columnIndex]
      if (field && newValue && newValue !== originalValue) {
        onManualCorrection(field, originalValue, newValue)
      }
    },
    [onManualCorrection]
  )

  const prepareAndSubmit = () => {
    const canvasDataGridData =
      (canvasRef.current && canvasRef.current.data) || []
    const mappedData = canvasDataGridData.map((row) => ({
      nChassis: row.nChassis,
      nParc: row.nParc,
      vehicleType: row.typeVehicule,
      operator: row.operateur,
      member: row.beneficiaire,
      depot: row.depot,
      location: row.lieu,
    }))
    onConfirm(mappedData, details)
  }

  return (
    <Grid
      container
      direction="column"
      justifyContent="space-between"
      spacing={2}
    >
      <Grid item>
        <Typography variant="h5">Prévisualisation et edition</Typography>
      </Grid>

      {/* Error banner */}
      {errors.count > 0 && (
        <Grid
          item
          style={{ display: 'flex', flexDirection: 'column', gap: '8px' }}
        >
          <NoticeBanner
            message={
              validationErrors && validationErrors.length === 0
                ? t(
                    'create-vehicle-type-dialog.bulk-upload-wizard.preview-error-label',
                    { nErrors: errors.count }
                  )
                : t(
                    'create-chassis-dialog.bulk-upload-wizard.validation-errors-hint',
                    { nErrors: errors.count }
                  )
            }
            color={theme.palette.danger.main}
            IconElement={WarningIcon}
          />
        </Grid>
      )}

      {/* Auto-corrections banner */}
      {autoCorrectionsCount > 0 && (
        <Grid
          item
          style={{ display: 'flex', flexDirection: 'column', gap: '8px' }}
        >
          <NoticeBanner
            message={t(
              'create-chassis-dialog.bulk-upload-wizard.auto-corrections-hint',
              { nCorrections: autoCorrectionsCount }
            )}
            color={theme.palette.warning.dark}
            IconElement={WarningIcon}
          />
        </Grid>
      )}

      {/* Suggestions banner */}
      {suggestionsCount > 0 && (
        <Grid
          item
          style={{ display: 'flex', flexDirection: 'column', gap: '8px' }}
        >
          <NoticeBanner
            message={t(
              'create-chassis-dialog.bulk-upload-wizard.suggestions-hint',
              { nSuggestions: suggestionsCount }
            )}
            color={theme.palette.warning.light}
            IconElement={WarningIcon}
          />
        </Grid>
      )}

      {/* Point 2: Corrections summary panel */}
      {totalCorrectionsCount > 0 && (
        <Grid item>
          <Accordion
            disableGutters
            elevation={0}
            sx={{
              border: `1px solid ${theme.palette.divider}`,
              borderRadius: 1,
            }}
            TransitionProps={{
              timeout: 0,
              onEnter: blockGridPointerEvents,
              onEntered: restoreGridPointerEvents,
              onExit: blockGridPointerEvents,
              onExited: restoreGridPointerEvents,
            }}
          >
            <AccordionSummary expandIcon={<ExpandMoreIcon />}>
              <Typography variant="body2" fontWeight={500}>
                {t(
                  'create-chassis-dialog.bulk-upload-wizard.corrections-summary-title',
                  { n: totalCorrectionsCount }
                )}
              </Typography>
            </AccordionSummary>
            <AccordionDetails sx={{ pt: 0 }}>
              {autoCorrectionsCount > 0 && (
                <>
                  <Typography
                    variant="caption"
                    color="text.secondary"
                    sx={{ display: 'block', mb: 0.5 }}
                  >
                    {t(
                      'create-chassis-dialog.bulk-upload-wizard.corrections-auto-section'
                    )}
                  </Typography>
                  <List dense disablePadding>
                    {autoCorrections.map((c, i) => (
                      <ListItem key={i} disableGutters sx={{ py: 0 }}>
                        <ListItemText
                          primaryTypographyProps={{ variant: 'body2' }}
                          primary={t(
                            'create-chassis-dialog.bulk-upload-wizard.corrections-summary-row',
                            {
                              row: c.row,
                              field: fieldLabels[c.field] || c.field,
                              original: c.originalValue,
                              corrected: c.correctedValue,
                            }
                          )}
                        />
                      </ListItem>
                    ))}
                  </List>
                </>
              )}
              {suggestionsCount > 0 && (
                <>
                  <Typography
                    variant="caption"
                    color="text.secondary"
                    sx={{
                      display: 'block',
                      mt: autoCorrectionsCount > 0 ? 1 : 0,
                      mb: 0.5,
                    }}
                  >
                    {t(
                      'create-chassis-dialog.bulk-upload-wizard.corrections-suggestions-section'
                    )}
                  </Typography>
                  <List dense disablePadding>
                    {suggestionCorrections.map((c, i) => (
                      <ListItem key={i} disableGutters sx={{ py: 0 }}>
                        <ListItemText
                          primaryTypographyProps={{ variant: 'body2' }}
                          primary={t(
                            'create-chassis-dialog.bulk-upload-wizard.corrections-summary-row',
                            {
                              row: c.row,
                              field: fieldLabels[c.field] || c.field,
                              original: c.originalValue,
                              corrected: c.correctedValue,
                            }
                          )}
                        />
                      </ListItem>
                    ))}
                  </List>
                </>
              )}
            </AccordionDetails>
          </Accordion>
        </Grid>
      )}

      <Grid item className={classes.gridContainer} ref={gridContainerRef}>
        {workbookData && (
          <CanvasDatagrid
            ref={canvasRef}
            data={workbookData}
            onCellEdited={(row, column) => {
              const key = `${row}-${column}`
              setErrors((prevErrors) => {
                const newSet = new Set(prevErrors.set)
                newSet.delete(key)
                const newErrorMap = { ...prevErrors.validationErrorMap }
                delete newErrorMap[key]
                return {
                  set: newSet,
                  count: newSet.size,
                  validationErrorMap: newErrorMap,
                }
              })
              setCorrectionMap((prev) => {
                const next = { ...prev }
                delete next[key]
                return next
              })
            }}
            onEditSuccess={() => {}}
            onEditFailure={() => {}}
            onAddRow={(rowIndex) => {
              setErrors((errors) => {
                const newSet = new Set(errors.set)
                newSet.add(`${rowIndex}-0`)
                newSet.add(`${rowIndex}-1`)
                newSet.add(`${rowIndex}-2`)
                return { ...errors, set: newSet, count: newSet.size }
              })
            }}
            onDeleteRow={(rowIndex) => {
              setErrors((errors) => {
                const newSet = new Set(
                  [...errors.set].filter(
                    (key) => !key.startsWith(`${rowIndex}-`)
                  )
                )
                const newErrorMap = { ...errors.validationErrorMap }
                Object.keys(newErrorMap).forEach((key) => {
                  if (key.startsWith(`${rowIndex}-`)) delete newErrorMap[key]
                })
                return {
                  ...errors,
                  set: newSet,
                  count: newSet.size,
                  validationErrorMap: newErrorMap,
                }
              })
              setCorrectionMap((prev) => {
                const next = { ...prev }
                Object.keys(next).forEach((key) => {
                  if (key.startsWith(`${rowIndex}-`)) delete next[key]
                })
                return next
              })
            }}
            errors={errors.set}
            errorMessages={errors.validationErrorMap}
            corrections={correctionMap}
            onRevertCorrection={handleRevertCorrection}
            onManualCorrection={handleManualCorrection}
          />
        )}
      </Grid>

      <Grid item container justifyContent="flex-end">
        <Button
          variant="contained"
          color="primary"
          disabled={errors.count > 0}
          onClick={prepareAndSubmit}
        >
          Importer
        </Button>
      </Grid>
    </Grid>
  )
}
